import React from "react";
import { Coins, Eye, Settings } from "lucide-react";
import {
  Affix,
  Card,
  CardBody,
  CardHeader,
  Callout,
  Field,
  Input,
  KV,
  Seg,
  ToggleRow,
} from "../components/primitives";

const EarningRulesTab = ({ config, setConfig, readOnly }) => {
  if (!config) return null;
  const earn = config.earn_config || {};
  const set = (k, v) =>
    setConfig({ ...config, earn_config: { ...earn, [k]: v } });

  const examplePts = Math.floor((1000 / (earn.per || 100)) * (earn.rate || 1));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[17px] font-bold text-[#0F172A]">Earning Rules</h2>
        <p className="text-[13px] text-[#64748B] mt-1 max-w-[700px]">
          Define how a customer accumulates loyalty points when they spend. The base rate applies everywhere.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        <div className="space-y-4">
          <Card>
            <CardHeader
              icon={<Coins size={18} />}
              tone="gold"
              title="Base earning rate"
              desc="Points granted per amount spent"
            />
            <CardBody>
              <div className="flex items-end gap-3 flex-wrap">
                <Field label="Points earned" style={{ width: 130 }}>
                  <Input
                    type="number"
                    value={earn.rate ?? 1}
                    onChange={(e) => set("rate", Number(e.target.value))}
                    disabled={readOnly}
                  />
                </Field>
                <div className="pb-[10px] text-[12px] font-semibold text-[#64748B]">
                  point(s) for every
                </div>
                <Field label="Amount spent" style={{ width: 160 }}>
                  <Affix pre="₹">
                    <Input
                      type="number"
                      value={earn.per ?? 100}
                      onChange={(e) => set("per", Number(e.target.value))}
                      style={{ paddingLeft: 28 }}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
              </div>
              <div className="h-px bg-[#EEF1F5] my-4" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Rounding" help="How fractional points on a bill are handled.">
                  <Seg
                    value={earn.rounding || "down"}
                    onChange={(v) => set("rounding", v)}
                    disabled={readOnly}
                    options={[
                      { value: "down", label: "Round down" },
                      { value: "nearest", label: "Nearest" },
                      { value: "up", label: "Round up" },
                    ]}
                  />
                </Field>
                <Field label="Minimum bill to earn" help="Bills below this amount earn nothing.">
                  <Affix pre="₹">
                    <Input
                      type="number"
                      value={earn.min_bill ?? 0}
                      onChange={(e) => set("min_bill", Number(e.target.value))}
                      style={{ paddingLeft: 28 }}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader icon={<Settings size={18} />} tone="amber" title="Exclusions & bonuses" />
            <CardBody className="py-1">
              <ToggleRow
                title="Don't earn points on discounted items"
                desc="Items already sold via a coupon/scheme won't accrue."
                on={!!earn.exclude_offers}
                onChange={(v) => set("exclude_offers", v)}
                disabled={readOnly}
              />
              <ToggleRow
                title="Exclude tax (GST) from earning base"
                desc="Calculate points on the pre-tax amount only."
                on={!!earn.exclude_tax}
                onChange={(v) => set("exclude_tax", v)}
                disabled={readOnly}
              />
              <div className="flex items-center gap-3 py-[13px] border-b border-[#EEF1F5] last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#0F172A]">Signup bonus</div>
                  <div className="text-[12px] text-[#64748B] mt-0.5 leading-snug">
                    One-time points credited when a new customer is created.
                  </div>
                </div>
                <div style={{ width: 130 }}>
                  <Affix post="pts">
                    <Input
                      type="number"
                      value={earn.signup_bonus ?? 0}
                      onChange={(e) => set("signup_bonus", Number(e.target.value))}
                      disabled={readOnly}
                    />
                  </Affix>
                </div>
              </div>
              <div className="flex items-center gap-3 py-[13px]">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#0F172A]">Points expiry</div>
                  <div className="text-[12px] text-[#64748B] mt-0.5 leading-snug">
                    Unused points lapse after this period.
                  </div>
                </div>
                <div style={{ width: 160 }}>
                  <select
                    value={earn.expiry_months ?? 12}
                    onChange={(e) => set("expiry_months", Number(e.target.value))}
                    disabled={readOnly}
                    className={`h-9 px-3 text-[13px] bg-white border border-[#D9DEE6] rounded-md outline-none w-full focus:border-primary focus:ring-2 focus:ring-primary/10 ${
                      readOnly ? "bg-[#F1F4F8] cursor-not-allowed" : ""
                    }`}
                  >
                    <option value={6}>6 months</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    <option value={0}>Never expire</option>
                  </select>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader icon={<Eye size={18} />} tone="green" title="Live example" />
            <CardBody>
              <Callout tone="green">
                <div className="text-[12px] font-semibold mb-1">On a ₹1,000 bill, a member earns</div>
                <div className="text-[32px] font-bold leading-none tracking-tight" style={{ color: "#15803C" }}>
                  {examplePts} pts
                </div>
                <div className="text-[12px] mt-1">
                  Rate: {earn.rate || 0} pt / ₹{earn.per || 0}
                </div>
              </Callout>
              <div className="pt-3 space-y-0">
                <KV label="Signup bonus" value={`${earn.signup_bonus || 0} pts`} />
                <KV
                  label="Expiry"
                  value={earn.expiry_months ? `${earn.expiry_months} mo` : "Never"}
                />
                <KV label="Excludes offer items" value={earn.exclude_offers ? "Yes" : "No"} />
                <KV label="Excludes tax" value={earn.exclude_tax ? "Yes" : "No"} />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EarningRulesTab;
